import {
  Calendar,
  Clock,
  MapPin,
  AlertCircle,
  Lock,
  Users,
  CalendarCheck,
  Globe,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { NavLink } from "react-router-dom";

const formatDate = (date: string, language: string) => {
  const [day, month, year] = date.split(" ");
  if (language === "en") {
    const monthsMap: { [key: string]: string } = {
      stycznia: "January",
      lutego: "February",
      marca: "March",
      kwietnia: "April",
      maja: "May",
      czerwca: "June",
      lipca: "July",
      sierpnia: "August",
      września: "September",
      października: "October",
      listopada: "November",
      grudnia: "December",
    };
    return `${monthsMap[month]} ${day}, ${year}`;
  }
  return date;
};

const EventCard = ({
  date,
  location,
  time,
  title,
  description = "",
  highlight = false,
  link,
  showBuyButton = false,
}: {
  date: string;
  location: string;
  time: string;
  title: string;
  description?: string;
  highlight?: boolean;
  link?: string;
  showBuyButton?: boolean;
}) => {
  const { language, t } = useLanguage();
  const formattedDate = formatDate(date, language);

  const getTranslatedDescription = (desc: string) => {
    if (desc === "Spotkanie zamknięte") return t("events.closed");
    if (desc === "Spotkanie otwarte") return t("events.open");
    if (desc === "Szczegóły wkrótce") return t("events.details");
    if (desc === "Spotkanie otwarte dla wszystkich") return t("events.open");
    if (desc === "Kulminacyjne wydarzenie wizyty Sama Childersa w Polsce")
      return t("final.description");
    return desc;
  };

  const CardContent = () => (
    <div className="p-6">
      <div className="flex items-center mb-3">
        <Calendar className="mr-2 h-5 w-5" />
        <span className="font-medium">{formattedDate}</span>
      </div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <div className="flex items-center mb-2 opacity-90">
        <MapPin className="mr-2 h-4 w-4" />
        <span>{location}</span>
      </div>
      <div className="flex items-center mb-3 opacity-90">
        <Clock className="mr-2 h-4 w-4" />
        <span>{time}</span>
      </div>
      {description && (
        <p className="text-sm mt-2">{getTranslatedDescription(description)}</p>
      )}
      {showBuyButton && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 block w-full"
        >
          <button className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
            highlight 
              ? "bg-white text-primary hover:bg-white/90" 
              : "bg-primary text-primary-foreground hover:bg-primary/90"
          }`}>
            {t("nav.buyTicket")}
          </button>
        </a>
      )}
    </div>
  );

  const cardClasses = `rounded-lg shadow-md overflow-hidden ${
    highlight
      ? "bg-primary text-primary-foreground"
      : "bg-card border border-border"
  }`;

  if (link && !showBuyButton) {
    return (
      <NavLink
        to={link}
        className={`block transition-transform hover:scale-[1.02] ${cardClasses}`}
      >
        <CardContent />
      </NavLink>
    );
  }

  return (
    <div className={cardClasses}>
      <CardContent />
    </div>
  );
};

const EventsPage = () => {
  const { t } = useLanguage();

  const events = [
    // 7 maja
    {
      date: "7 maja 2025",
      title: "ZK Wronki",
      location: "Wronki",
      time: "10:00",
      description: "Spotkanie zamknięte",
    },
    {
      date: "7 maja 2025",
      title: "KZ Swarzędz",
      location: "Swarzędz",
      time: "18:00",
      description: "Spotkanie otwarte",
    },

    // 8 maja
    {
      date: "8 maja 2025",
      title: "ZK Gębarzewo",
      location: "Gębarzewo",
      time: "10:00",
      description: "Spotkanie zamknięte",
    },
    {
      date: "8 maja 2025",
      title: "ZK Koziegłowy",
      location: "Koziegłowy",
      time: "16:00",
      description: "Spotkanie zamknięte",
    },
    {
      date: "8 maja 2025",
      title: "Kościół Uliczny",
      location: "Poznań",
      time: "19:00",
      description: "Spotkanie otwarte dla wszystkich",
    },

    // 9 maja
    {
      date: "9 maja 2025",
      title: "KZ Oborniki",
      location: "Oborniki",
      time: "9:00",
      description: "Spotkanie otwarte",
    },
    {
      date: "9 maja 2025",
      title: "Baza wojsk USA",
      location: "Poznań",
      time: "14:00",
      description: "Szczegóły wkrótce",
    },
    {
      date: "9 maja 2025",
      title: "Sam Childers dla liderów biznesu",
      location: "Hotel Mercure",
      time: "17:00",
      description: "Spotkanie dla liderów biznesu",
      link: "https://biznes.kaznodziejazkarabinem.pl",
      showBuyButton: true,
    },

    // 10 maja
    {
      date: "10 maja 2025",
      title: "OZ Pobiedziska",
      location: "Pobiedziska",
      time: "10:00",
      description: "Spotkanie zamknięte",
    },
    {
      date: "10 maja 2025",
      title: "Wielki Finał",
      location: "Stadion Miejski, Poznań, sala B17",
      time: "17:00",
      description: "Kulminacyjne wydarzenie wizyty Sama Childersa w Polsce",
      highlight: true,
      link: "/buy-ticket",
      showBuyButton: true,
    },

    // 11 maja
    {
      date: "11 maja 2025",
      title: "Arka",
      location: "Poznań",
      time: "11:00",
      description: "Spotkanie otwarte",
    },
    {
      date: "11 maja 2025",
      title: "KB + KZU",
      location: "Poznań",
      time: "16:00",
      description: "Spotkanie otwarte",
    },
  ];

  return (
    <div className="pt-16">
      <div className="bg-secondary">
        <div className="container mx-auto py-16 px-4 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t("events.title")}
          </h1>
          <p className="text-xl text-primary">{t("events.subtitle")}</p>
        </div>
      </div>

      <div className="container mx-auto py-12 px-4 md:px-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">
            {t("events.schedule.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("events.schedule.description")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event, index) => (
            <EventCard
              key={index}
              date={event.date}
              title={event.title}
              location={event.location}
              time={event.time}
              description={event.description}
              highlight={event.highlight}
              link={event.link}
              showBuyButton={event.showBuyButton}
            />
          ))}
        </div>

        <div className="mt-12 p-6 bg-secondary rounded-lg">
          <h3 className="text-xl font-bold mb-3">
            {t("events.important.title")}
          </h3>
          <ul className="space-y-4">
            <li className="flex items-start">
              <AlertCircle className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
              <span>{t("events.important.schedule")}</span>
            </li>
            <li className="flex items-start">
              <Lock className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
              <span>{t("events.important.closed")}</span>
            </li>
            <li className="flex items-start">
              <Users className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
              <span>{t("events.important.open")}</span>
            </li>
            <li className="flex items-start">
              <CalendarCheck className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
              <span>{t("events.important.final")}</span>
            </li>
            <li className="flex items-start">
              <Globe className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
              <span>{t("events.important.translation")}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
